import { Frame } from 'dragonmouse-shared/interfaces';
import {
	resolveOptimisticallyUpdatedFrames,
	setSceneLoadError,
	updateFrames,
} from '../../redux/db/actions';
import store from '../../redux/store';
import { VITE_FORGE_URL } from '../../constants';
import { FrameRow } from '../types';
import { parsePostgresDate, processFrameEntries } from '../util';
import { Stream } from './Stream';
import { v4 as uuid } from 'uuid';
import { ChangeMessage, FetchError, isChangeMessage, Message, Row } from '@electric-sql/client';

export class ActiveSceneFramesStream extends Stream<FrameRow> {
	constructor(activeSceneId: string) {
		const url = VITE_FORGE_URL + `/electric/scene/${activeSceneId}/frames`;

		const onError = (error: Error) => {
			if (error instanceof FetchError && error.status === 403) {
				// When the server says these scene-id-dependent streams are denied, that
				// could mean the scene has been deleted or the user has been removed from it.
				store.dispatch(setSceneLoadError('You do not have access to this project'));
				store.dispatch(updateFrames([]));
			}
			console.error(error);
		};

		const params = {
			shapeBust: uuid(),
		};
		super(url, params, onError, 'activeScene.frames');
	}

	onShapeData(rows: FrameRow[]) {
		const frames: Frame[] = rows.map((frameRow) => ({
			id: frameRow.id,
			sceneId: frameRow.scene_id,
			title: frameRow.title,
			x: frameRow.x,
			y: frameRow.y,
			width: frameRow.width,
			height: frameRow.height,
			imagePath: frameRow.image_path,
			siteId: frameRow.site_id,
			entries: frameRow.entries ? processFrameEntries(frameRow.entries) : undefined,
			metadata: frameRow.metadata,
			createdAt: parsePostgresDate(frameRow.created_at),
			deletedAt: frameRow.deleted_at ? parsePostgresDate(frameRow.deleted_at) : null,
			writeId: frameRow.write_id,
			// Check if this frame's scene is in the user's scenes collection
			// If scenes aren't loaded yet, this will default to viewOnly (fail gracefully)
			// When scenes do load, refreshFramePermissions will update these flags
			viewOnly: !store.getState().db.scenes.some((scene) => scene.id === frameRow.scene_id),
		}));
		store.dispatch(updateFrames(frames));
	}

	onStreamMessages(messages: Array<unknown>) {
		const writeIds = messages
			.filter((msg): msg is ChangeMessage<FrameRow> => isChangeMessage(msg as Message<Row<never>>))
			.map((message): string => {
				return message.value.write_id || `delete:${message.value.id}`;
			});
		store.dispatch(resolveOptimisticallyUpdatedFrames(writeIds));
	}
}
